import { useState, useEffect } from "@wordpress/element";
import { __ } from "@wordpress/i18n";
import Constants from "../constants";

const Filters = ({
	filtersData,
	setFiltersData,
	onApplyFilters,
	jobTypes,
	experienceLevels,
	phrases,
}) => {
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState(filtersData.Keyword);

	// Handle debounced search
	useEffect(() => {
		const delayDebounceFn = setTimeout(() => {
			if (searchTerm !== filtersData.Keyword) {
				setFiltersData({
					...filtersData,
					Keyword: searchTerm,
				});
				onApplyFilters();
			}
		}, 500); // 500ms delay

		return () => clearTimeout(delayDebounceFn);
	}, [searchTerm]);

	const handleKeyDown = (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			setFiltersData({
				...filtersData,
				Keyword: searchTerm,
			});
			onApplyFilters();
		}
	};

	const handleOnApplyFilters = () => {
		onApplyFilters();
		setIsPopupOpen(false);
	};

	const handleClearFilters = () => {
		setFiltersData({
			Keyword: "",
			SortBy: "",
			JobType: "",
			ExperienceLevel: "",
			MinSalary: "0",
			MaxSalary: "1000000",
			Currency: "",
			SortOrder: "",
		});
		onApplyFilters();
		setIsPopupOpen(false);
	};

	return (
		<>
			<div className="relative flex flex-col md:flex-row items-start md:items-center justify-start md:justify-between filter-controls">
				<div className="flex items-center mb-4 md:mb-0">
					<button
						className="me-1 lg:me-5 h-12 px-1.5 lg:px-5 rtl:flex-row-reverse border-0 py-1.5 transition focus:ring-2 focus:ring-inset appearance-none bg-lightGray bg-no-repeat rounded-lg justify-center items-center text-body text-sm font-medium gap-3.5 inline-flex"
						onClick={(e) => setIsPopupOpen(true)}
					>
						{__(phrases.filters, Constants.TEXT_DOMAIN)}
						<svg
							width="20"
							height="20"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M6 3.25C3.932 3.25 2.25 4.932 2.25 7C2.25 9.068 3.932 10.75 6 10.75C8.068 10.75 9.75 9.068 9.75 7C9.75 4.932 8.068 3.25 6 3.25ZM6 9.25C4.759 9.25 3.75 8.241 3.75 7C3.75 5.759 4.759 4.75 6 4.75C7.241 4.75 8.25 5.759 8.25 7C8.25 8.241 7.241 9.25 6 9.25ZM18 13.25C15.932 13.25 14.25 14.932 14.25 17C14.25 19.068 15.932 20.75 18 20.75C20.068 20.75 21.75 19.068 21.75 17C21.75 14.932 20.068 13.25 18 13.25ZM18 19.25C16.759 19.25 15.75 18.241 15.75 17C15.75 15.759 16.759 14.75 18 14.75C19.241 14.75 20.25 15.759 20.25 17C20.25 18.241 19.241 19.25 18 19.25ZM15 7.75H21C21.414 7.75 21.75 7.414 21.75 7C21.75 6.586 21.414 6.25 21 6.25H15C14.586 6.25 14.25 6.586 14.25 7C14.25 7.414 14.586 7.75 15 7.75ZM9 16.25H3C2.586 16.25 2.25 16.586 2.25 17C2.25 17.414 2.586 17.75 3 17.75H9C9.414 17.75 9.75 17.414 9.75 17C9.75 16.586 9.414 16.25 9 16.25Z"
								fill="#F67748"
							/>
						</svg>
					</button>
					<select
						className="sortBySelector h-12 ltr:pe-6 ltr:ps-5 rtl:pe-5 rtl:ps-10 border-0 py-1.5 transition focus:ring-2 focus:ring-inset appearance-none bg-lightGray bg-no-repeat rounded-lg justify-center items-center text-body text-sm font-medium gap-3.5 inline-flex"
						onChange={(e) =>
							setFiltersData({
								...filtersData,
								SortBy: e.target.value,
							})
						}
						value={filtersData.SortBy}
					>
						<option value="">
							{__(phrases.sort_by, Constants.TEXT_DOMAIN)}
						</option>
						<option value="date">
							{__(phrases.date, Constants.TEXT_DOMAIN)}
						</option>
						<option value="datePosted">
							{__(phrases.date_posted, Constants.TEXT_DOMAIN)}
						</option>
					</select>
				</div>
				<input
					type="text"
					placeholder={__(phrases.search_jobs, Constants.TEXT_DOMAIN)}
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					onKeyDown={handleKeyDown}
					className="block w-full h-12 max-w-sm px-1.5 lg:px-5 py-1 transition border-0 rounded-md bg-lightGray text-body focus:ring-2 focus:ring-inset sm:leading-6"
				/>

				{isPopupOpen && (
					<div className="absolute top-0 w-full max-w-sm p-6 bg-white rounded-lg shadow-md ltr:left-0 rtl:right-0">
						<button
							className="absolute text-2xl text-gray-500 transition-all top-4 rtl:left-4 ltr:right-4 ms-5 hover:text-red-600 p-2"
							aria-label="Close"
							onClick={() => {
								setIsPopupOpen(false);
							}}
						>
							<i className="fa-solid fa-xmark"></i>
						</button>

						<h2 className="mb-6 text-xl font-bold text-gray-800">
							{__(phrases.filters, Constants.TEXT_DOMAIN)}
						</h2>

						<div className="space-y-6">
							{/* Job Types */}
							<div>
								<label className="block mb-2 font-semibold text-gray-700">
									{__(phrases.job_type, Constants.TEXT_DOMAIN)}
								</label>
								<div className="space-y-2">
									{jobTypes.map((jobType, index) => (
										<div key={index} className="flex items-center">
											<input
												type="checkbox"
												id={`jobType-${index}`}
												className="w-4 h-4 me-2 accent-primary text-primary border-gray-300 rounded focus:ring-primary"
												checked={filtersData.JobType === jobType.name}
												onChange={(e) =>
													setFiltersData({
														...filtersData,
														JobType: e.target.checked
															? jobType.id.toString()
															: "",
													})
												}
											/>
											<label
												htmlFor={`jobType-${index}`}
												className="text-gray-700"
											>
												{jobType.name}
											</label>
										</div>
									))}
								</div>
							</div>

							{/* Experience Level */}
							<div>
								<label className="block mb-2 font-semibold text-gray-700">
									{__(phrases.experience_level, Constants.TEXT_DOMAIN)}
								</label>
								<div className="space-y-2">
									{experienceLevels &&
										experienceLevels.map((level) => (
											<div key={level.id} className="flex items-center">
												<input
													type="checkbox"
													id={`experience-${level.id}`}
													className="w-4 h-4 me-2 accent-primary text-primary border-gray-300 rounded focus:ring-primary"
													checked={
														filtersData.experienceLevel === level.id.toString()
													}
													onChange={(e) =>
														setFiltersData({
															...filtersData,
															ExperienceLevel: e.target.checked
																? level.id.toString()
																: "",
														})
													}
												/>
												<label
													htmlFor={`experience-${level.id}`}
													className="text-gray-700"
												>
													{document.documentElement.lang.startsWith("ar")
														? level.nameAr
														: level.name}
												</label>
											</div>
										))}
								</div>
							</div>

							{/* Monthly Salary Range */}
							<div>
								<label className="block mb-2 font-semibold text-gray-700">
									{__(phrases.monthly_salary, Constants.TEXT_DOMAIN)}
								</label>
								<div className="relative pt-6">
									<div className="relative h-1 bg-gray-200 rounded-full">
										{/* Min Salary Handle */}
										<div
											className="absolute w-6 h-6 -mt-2 -ml-3 bg-black rounded-full cursor-pointer"
											style={{
												left: `${
													((filtersData.MinSalary || 0) / 1000000) * 100
												}%`,
											}}
											onMouseDown={(e) => {
												const startX = e.clientX;
												const startValue = filtersData.MinSalary || 0;

												const handleMouseMove = (moveEvent) => {
													const delta = moveEvent.clientX - startX;
													const range = 1000000;
													const step = 1000;
													const pixelsPerStep =
														e.target.parentElement.offsetWidth / (range / step);
													const steps = Math.round(delta / pixelsPerStep);
													const valueChange = steps * step;
													const newValue = Math.max(
														0,
														Math.min(
															filtersData.MaxSalary || 1000000,
															startValue + valueChange,
														),
													);

													// Round to nearest 1000
													const roundedValue =
														Math.round(newValue / 1000) * 1000;

													setFiltersData((prev) => ({
														...prev,
														MinSalary: roundedValue,
													}));
												};

												const handleMouseUp = () => {
													document.removeEventListener(
														"mousemove",
														handleMouseMove,
													);
													document.removeEventListener(
														"mouseup",
														handleMouseUp,
													);
												};

												document.addEventListener("mousemove", handleMouseMove);
												document.addEventListener("mouseup", handleMouseUp);
											}}
										/>

										{/* Max Salary Handle */}
										<div
											className="absolute w-6 h-6 -mt-2 -ml-3 bg-black rounded-full cursor-pointer"
											style={{
												left: `${
													((filtersData.MaxSalary || 1000000) / 1000000) * 100
												}%`,
											}}
											onMouseDown={(e) => {
												const startX = e.clientX;
												const startValue = filtersData.MaxSalary || 1000000;

												const handleMouseMove = (moveEvent) => {
													const delta = moveEvent.clientX - startX;
													const range = 1000000;
													const step = 1000;
													const pixelsPerStep =
														e.target.parentElement.offsetWidth / (range / step);
													const steps = Math.round(delta / pixelsPerStep);
													const valueChange = steps * step;
													const newValue = Math.max(
														filtersData.MinSalary || 0,
														Math.min(1000000, startValue + valueChange),
													);

													// Round to nearest 1000
													const roundedValue =
														Math.round(newValue / 1000) * 1000;

													setFiltersData((prev) => ({
														...prev,
														MaxSalary: roundedValue,
													}));
												};

												const handleMouseUp = () => {
													document.removeEventListener(
														"mousemove",
														handleMouseMove,
													);
													document.removeEventListener(
														"mouseup",
														handleMouseUp,
													);
												};

												document.addEventListener("mousemove", handleMouseMove);
												document.addEventListener("mouseup", handleMouseUp);
											}}
										/>

										{/* Selected Range */}
										<div
											className="absolute h-1 bg-black rounded-full"
											style={{
												left: `${
													((filtersData.MinSalary || 0) / 1000000) * 100
												}%`,
												width: `${
													(((filtersData.MaxSalary || 1000000) -
														(filtersData.MinSalary || 0)) /
														1000000) *
													100
												}%`,
											}}
										/>
									</div>

									{/* Value Labels */}
									<div className="flex justify-between mt-2 text-sm">
										<div className="px-2 py-1 text-white bg-black rounded-full">
											{(filtersData.MinSalary || 0).toLocaleString()}
										</div>
										<div className="px-2 py-1 text-white bg-black rounded-full">
											{(filtersData.MaxSalary || 1000000).toLocaleString()}
										</div>
									</div>
								</div>
							</div>

							<div className="mt-6 flex justify-between">
								<button
									onClick={handleClearFilters}
									className="px-4 py-2 text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
								>
									{__(
										phrases.clear_filters || "Clear Filters",
										Constants.TEXT_DOMAIN,
									)}
								</button>
								<button
									onClick={handleOnApplyFilters}
									className="px-4 py-2 text-white bg-primary rounded hover:bg-darkPrimary transition-colors"
								>
									{__(phrases.apply_filters, Constants.TEXT_DOMAIN)}
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		</>
	);
};

export default Filters;
